import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Card,
  Typography,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Chip,
  IconButton,
  Alert,
  Snackbar,
  Grid,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack, Add, Save, Delete, Edit } from '@mui/icons-material';
import { campaignCategoryService, extractApiDataList } from '../services/api';

const emptyForm = { name: '', slug: '', description: '', display_order: '0', is_active: '1' };

const CampaignCategoriesPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [newRow, setNewRow] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [editRow, setEditRow] = useState(emptyForm);
  const [toast, setToast] = useState({ open: false, message: '', type: 'success' });
  const [loadError, setLoadError] = useState('');

  const load = async () => {
    try {
      setLoading(true);
      setLoadError('');
      const res = await campaignCategoryService.getAll({ include_inactive: 1 });
      setCategories(extractApiDataList(res));
    } catch (e) {
      console.error(e);
      const apiErr = e.response?.data?.error || e.message || 'Failed to load categories';
      const isRouteMissing =
        e.response?.status === 404 &&
        (String(apiErr).toLowerCase().includes('route not found') ||
          String(e.response?.data?.path || '').includes('campaign-categories'));
      setLoadError(
        isRouteMissing
          ? 'Categories API is not available on this server. Use local API (REACT_APP_API_BASE_URL=http://localhost:5000/api) and restart admin, or deploy the latest API with /api/campaign-categories.'
          : apiErr
      );
      setToast({ open: true, message: isRouteMissing ? 'Categories API not deployed on server' : apiErr, type: 'error' });
      setCategories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleAdd = async () => {
    if (!newRow.name.trim()) {
      setToast({ open: true, message: 'Name is required', type: 'error' });
      return;
    }
    try {
      await campaignCategoryService.create({
        name: newRow.name.trim(),
        slug: newRow.slug.trim() || undefined,
        description: newRow.description.trim() || null,
        display_order: newRow.display_order === '' ? 0 : parseInt(newRow.display_order, 10),
        is_active: newRow.is_active === '1',
      });
      setNewRow(emptyForm);
      setToast({ open: true, message: 'Category added', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Add failed', type: 'error' });
    }
  };

  const startEdit = (row) => {
    setEditId(row.id);
    setEditRow({
      name: row.name || '',
      slug: row.slug || '',
      description: row.description || '',
      display_order: String(row.display_order ?? 0),
      is_active: row.is_active === 0 || row.is_active === false ? '0' : '1',
    });
  };

  const handleSaveEdit = async () => {
    if (!editId) return;
    try {
      await campaignCategoryService.update(editId, {
        name: editRow.name.trim(),
        slug: editRow.slug.trim() || undefined,
        description: editRow.description.trim() || null,
        display_order: editRow.display_order === '' ? 0 : parseInt(editRow.display_order, 10),
        is_active: editRow.is_active === '1',
      });
      setEditId(null);
      setToast({ open: true, message: 'Category updated', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Update failed', type: 'error' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category? Linked campaigns will be unassigned.')) return;
    try {
      await campaignCategoryService.delete(id);
      setToast({ open: true, message: 'Category deleted', type: 'success' });
      await load();
    } catch (e) {
      setToast({ open: true, message: e.response?.data?.error || 'Delete failed', type: 'error' });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#f5f7fa', minHeight: '100vh', py: 4 }}>
      <Container maxWidth={false} sx={{ maxWidth: '100%' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/campaigns')} sx={{ mb: 2, color: '#2c5f2d' }}>
          Back to Campaigns
        </Button>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Campaign Categories
        </Typography>
        <Typography variant="body2" sx={{ color: '#667085', mb: 3 }}>
          Master data for grouping campaigns (used in admin and public campaign listing).
        </Typography>

        {loadError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {loadError}
          </Alert>
        )}

        <Card sx={{ p: 3, mb: 3, border: '1px solid #e8ecf1' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
            Add category
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Name *" value={newRow.name} onChange={(e) => setNewRow((s) => ({ ...s, name: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField fullWidth size="small" label="Slug (optional)" value={newRow.slug} onChange={(e) => setNewRow((s) => ({ ...s, slug: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField fullWidth size="small" label="Description" value={newRow.description} onChange={(e) => setNewRow((s) => ({ ...s, description: e.target.value }))} />
            </Grid>
            <Grid item xs={6} md={1}>
              <TextField fullWidth size="small" label="Order" type="number" value={newRow.display_order} onChange={(e) => setNewRow((s) => ({ ...s, display_order: e.target.value }))} />
            </Grid>
            <Grid item xs={6} md={1}>
              <TextField fullWidth size="small" select label="Active" value={newRow.is_active} onChange={(e) => setNewRow((s) => ({ ...s, is_active: e.target.value }))} SelectProps={{ native: true }}>
                <option value="1">Yes</option>
                <option value="0">No</option>
              </TextField>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button variant="contained" startIcon={<Add />} onClick={handleAdd} sx={{ backgroundColor: '#2c5f2d' }}>
                Add
              </Button>
            </Grid>
          </Grid>
        </Card>

        <Card sx={{ border: '1px solid #e8ecf1' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: '#f8fafb' }}>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Slug</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 4, color: '#667085' }}>
                      No categories yet. Run migration SQL or add one above.
                    </TableCell>
                  </TableRow>
                ) : (
                  categories.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.id}</TableCell>
                      <TableCell>
                        {editId === row.id ? (
                          <TextField size="small" value={editRow.name} onChange={(e) => setEditRow((s) => ({ ...s, name: e.target.value }))} />
                        ) : (
                          row.name
                        )}
                      </TableCell>
                      <TableCell>
                        {editId === row.id ? (
                          <TextField size="small" value={editRow.slug} onChange={(e) => setEditRow((s) => ({ ...s, slug: e.target.value }))} />
                        ) : (
                          <code>{row.slug}</code>
                        )}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        {editId === row.id ? (
                          <TextField size="small" fullWidth value={editRow.description} onChange={(e) => setEditRow((s) => ({ ...s, description: e.target.value }))} />
                        ) : (
                          row.description || '—'
                        )}
                      </TableCell>
                      <TableCell>
                        {editId === row.id ? (
                          <TextField size="small" type="number" value={editRow.display_order} onChange={(e) => setEditRow((s) => ({ ...s, display_order: e.target.value }))} />
                        ) : (
                          row.display_order
                        )}
                      </TableCell>
                      <TableCell>
                        {editId === row.id ? (
                          <TextField size="small" select value={editRow.is_active} onChange={(e) => setEditRow((s) => ({ ...s, is_active: e.target.value }))} SelectProps={{ native: true }}>
                            <option value="1">Active</option>
                            <option value="0">Inactive</option>
                          </TextField>
                        ) : (
                          <Chip label={row.is_active ? 'Active' : 'Inactive'} size="small" color={row.is_active ? 'success' : 'default'} />
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {editId === row.id ? (
                          <>
                            <IconButton size="small" color="primary" onClick={handleSaveEdit} aria-label="Save">
                              <Save fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => setEditId(null)} aria-label="Cancel">
                              ×
                            </IconButton>
                          </>
                        ) : (
                          <>
                            <IconButton size="small" onClick={() => startEdit(row)} aria-label="Edit">
                              <Edit fontSize="small" />
                            </IconButton>
                            <IconButton size="small" color="error" onClick={() => handleDelete(row.id)} aria-label="Delete">
                              <Delete fontSize="small" />
                            </IconButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Alert severity="info" sx={{ mt: 3 }}>
          Assign a category when creating or editing a campaign. Inactive categories are hidden on the public site filter.
        </Alert>
      </Container>

      <Snackbar open={toast.open} autoHideDuration={5000} onClose={() => setToast((t) => ({ ...t, open: false }))}>
        <Alert severity={toast.type} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CampaignCategoriesPage;
